import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GitHubIcon from '@mui/icons-material/GitHub';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RobotViewer3D, { RobotViewer3DHandle } from '../components/RobotViewer3D';
import FloatingJointInspector from '../components/FloatingJointInspector';
import JointTestOverlay from '../components/JointTestOverlay';
import { Robot, RobotJoint } from '../types';
import { uploadRobot, importRobotFromGithub, fetchRobot, updateRobot, uploadThumbnail } from '../api/robots';
import { extractRobotZip, revokeBlobUrls } from '../utils/zipExtractor';
import GitHubRepoPicker from '../components/GitHubRepoPicker';
import LinkedError from '../components/LinkedError';

function parseUrdfJoints(urdfContent: string): RobotJoint[] {
  const doc = new DOMParser().parseFromString(urdfContent, 'text/xml');
  const els = doc.getElementsByTagName('joint');
  const joints: RobotJoint[] = [];
  for (let i = 0; i < els.length; i++) {
    const el = els[i];
    const type = (el.getAttribute('type') || 'fixed') as RobotJoint['type'];
    const axisEl = el.getElementsByTagName('axis')[0];
    const axisParts = (axisEl?.getAttribute('xyz') || '0 0 1').split(/\s+/).map(Number);
    const limitEl = el.getElementsByTagName('limit')[0];
    const parentEl = el.getElementsByTagName('parent')[0];
    const childEl = el.getElementsByTagName('child')[0];
    joints.push({
      name: el.getAttribute('name') || '',
      type,
      axis: [axisParts[0] || 0, axisParts[1] || 0, axisParts[2] || 1] as [number, number, number],
      limits: {
        lower: parseFloat(limitEl?.getAttribute('lower') || '0'),
        upper: parseFloat(limitEl?.getAttribute('upper') || '0'),
        effort: parseFloat(limitEl?.getAttribute('effort') || '0'),
        velocity: parseFloat(limitEl?.getAttribute('velocity') || '0'),
      },
      parent_link: parentEl?.getAttribute('link') || '',
      child_link: childEl?.getAttribute('link') || '',
    });
  }
  return joints;
}

type Phase = 'drop' | 'converting' | 'viewer' | 'save';

interface ChecklistItem {
  label: string;
  status: 'pending' | 'done' | 'active' | 'error';
}

const RobotUpload: React.FC = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('drop');
  const [robotId, setRobotId] = useState<string | null>(null);
  const [robot, setRobot] = useState<Robot | null>(null);
  const [urdfContent, setUrdfContent] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [selectedJoint, setSelectedJoint] = useState<string | undefined>();
  const [jointValues, setJointValues] = useState<Record<string, number>>({});
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parsedJoints, setParsedJoints] = useState<RobotJoint[]>([]);
  const [githubUrl, setGithubUrl] = useState('');
  const [githubRef, setGithubRef] = useState('');
  const [importing, setImporting] = useState(false);
  const [meshBlobUrls, setMeshBlobUrls] = useState<Map<string, string>>(new Map());
  const meshBlobUrlsRef = useRef<Map<string, string>>(new Map());
  const viewerRef = useRef<RobotViewer3DHandle>(null);
  const thumbnailBlobRef = useRef<Blob | null>(null);

  // Test mode state
  const [testMode, setTestMode] = useState(false);
  const [testJointIndex, setTestJointIndex] = useState(0);
  const [testResults, setTestResults] = useState<Map<string, 'pass' | 'fail' | 'fixed'>>(new Map());
  const [inspectorCollapsed, setInspectorCollapsed] = useState(false);

  // Fix mode state
  const [fixingJoint, setFixingJoint] = useState(false);
  const [fixLimits, setFixLimits] = useState({ lower: 0, upper: 0 });
  const [fixAxisFlipped, setFixAxisFlipped] = useState(false);
  const [fixContinuous, setFixContinuous] = useState(false);
  const [jointFixes, setJointFixes] = useState<Map<string, { limits?: { lower: number; upper: number }; origLimits: { lower: number; upper: number }; axisFlipped: boolean; continuous?: boolean }>>(new Map());

  const articulableJoints = useMemo(
    () => parsedJoints.filter((j) => j.type === 'revolute' || j.type === 'prismatic' || j.type === 'continuous'),
    [parsedJoints],
  );

  const currentTestJoint = testMode && testJointIndex < articulableJoints.length ? articulableJoints[testJointIndex] : null;

  const getDefaultLimits = (j: typeof currentTestJoint) => {
    if (!j) return { lower: 0, upper: 0 };
    const isCont = j.type === 'continuous' && j.limits.lower === 0 && j.limits.upper === 0;
    return { lower: isCont ? 0 : j.limits.lower, upper: isCont ? Math.PI * 2 : j.limits.upper };
  };

  const isContinuousJoint = currentTestJoint?.type === 'continuous' &&
    currentTestJoint.limits.lower === 0 && currentTestJoint.limits.upper === 0;
  const effectiveContinuous = fixingJoint ? fixContinuous : isContinuousJoint;

  const animatingJoint = currentTestJoint
    ? {
        name: currentTestJoint.name,
        ...(fixingJoint
          ? fixContinuous ? { lower: 0, upper: Math.PI * 2 } : fixLimits
          : getDefaultLimits(currentTestJoint)),
        axisFlipped: fixingJoint ? fixAxisFlipped : false,
        continuous: effectiveContinuous,
      }
    : null;


  const handlePass = () => {
    if (!currentTestJoint) return;
    setJointValues((prev) => ({ ...prev, [currentTestJoint.name]: 0 }));
    setTestResults((prev) => new Map(prev).set(currentTestJoint.name, 'pass'));
    setTestJointIndex((prev) => prev + 1);
    setFixingJoint(false);
  };

  const handleStartFix = () => {
    if (!currentTestJoint) return;
    const defaults = getDefaultLimits(currentTestJoint);
    setFixLimits(defaults);
    setFixAxisFlipped(false);
    setFixContinuous(currentTestJoint.type === 'continuous');
    setFixingJoint(true);
  };

  const handleApplyFix = () => {
    if (!currentTestJoint) return;
    const origLimits = getDefaultLimits(currentTestJoint);
    const limitsChanged = !fixContinuous && (fixLimits.lower !== origLimits.lower || fixLimits.upper !== origLimits.upper);
    setJointFixes((prev) => new Map(prev).set(currentTestJoint.name, {
      limits: limitsChanged ? { ...fixLimits } : undefined,
      origLimits,
      axisFlipped: fixAxisFlipped,
      continuous: fixContinuous || undefined,
    }));
    setJointValues((prev) => ({ ...prev, [currentTestJoint.name]: 0 }));
    setTestResults((prev) => new Map(prev).set(currentTestJoint.name, 'fixed'));
    setTestJointIndex((prev) => prev + 1);
    setFixingJoint(false);
  };

  const handleSkipFix = () => {
    if (!currentTestJoint) return;
    setJointValues((prev) => ({ ...prev, [currentTestJoint.name]: 0 }));
    setTestResults((prev) => new Map(prev).set(currentTestJoint.name, 'fail'));
    setTestJointIndex((prev) => prev + 1);
    setFixingJoint(false);
  };

  const handleExitTest = () => {
    setTestMode(false);
    setFixingJoint(false);
    const zeroed: Record<string, number> = {};
    articulableJoints.forEach((j) => { zeroed[j.name] = 0; });
    setJointValues((prev) => ({ ...prev, ...zeroed }));
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      revokeBlobUrls(meshBlobUrlsRef.current);
    };
  }, []);

  const [checklist, setChecklist] = useState<ChecklistItem[]>([
    { label: 'Upload complete', status: 'pending' },
    { label: 'Parsing URDF', status: 'pending' },
    { label: 'Resolving meshes', status: 'pending' },
    { label: 'Converting to USD', status: 'pending' },
  ]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Revoke any previous blob URLs
    revokeBlobUrls(meshBlobUrlsRef.current);
    setMeshBlobUrls(new Map());

    // Read content for client-side preview
    if (file.name.endsWith('.zip')) {
      try {
        const result = await extractRobotZip(file);
        setUrdfContent(result.urdfContent);
        setParsedJoints(parseUrdfJoints(result.urdfContent));
        setMeshBlobUrls(result.meshBlobUrls);
        meshBlobUrlsRef.current = result.meshBlobUrls;
        setName(result.urdfFileName.replace(/\.\w+$/, ''));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to extract ZIP');
        return;
      }
    } else if (file.name.endsWith('.urdf')) {
      const text = await file.text();
      setUrdfContent(text);
      setParsedJoints(parseUrdfJoints(text));
      setName(file.name.replace(/\.\w+$/, ''));
    } else {
      setName(file.name.replace(/\.\w+$/, ''));
    }

    setPhase('converting');
    setChecklist((prev) => prev.map((item, i) => (i === 0 ? { ...item, status: 'active' } : item)));

    try {
      const res = await uploadRobot(file);
      setRobotId(res.id);
      setChecklist((prev) =>
        prev.map((item, i) => {
          if (i === 0) return { ...item, status: 'done' };
          if (i === 1) return { ...item, status: 'active' };
          return item;
        }),
      );
    } catch {
      setError('Upload failed. Please try again.');
      setPhase('drop');
    }
  }, []);

  // Poll for conversion status
  useEffect(() => {
    if (phase !== 'converting' || !robotId) return;

    let step = 1;
    const interval = setInterval(async () => {
      try {
        const r = await fetchRobot(robotId);
        setRobot(r);

        if (r.status === 'ready') {
          setChecklist((prev) => prev.map((item) => ({ ...item, status: 'done' as const })));
          clearInterval(interval);
          setTimeout(() => setPhase('viewer'), 500);
        } else if (r.status === 'failed') {
          setChecklist((prev) =>
            prev.map((item, i) =>
              i === step ? { ...item, status: 'error' } : item,
            ),
          );
          setError(r.error || 'Conversion failed');
          clearInterval(interval);
        } else {
          if (step < 3) {
            step++;
            setChecklist((prev) =>
              prev.map((item, i) => {
                if (i < step) return { ...item, status: 'done' };
                if (i === step) return { ...item, status: 'active' };
                return item;
              }),
            );
          }
        }
      } catch {
        // Ignore polling errors
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [phase, robotId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/xml': ['.urdf'],
      'application/zip': ['.zip'],
      'application/octet-stream': ['.usd', '.usda'],
    },
    maxSize: 100 * 1024 * 1024,
    multiple: false,
  });

  const handleSave = async () => {
    if (!robotId) return;
    try {
      const promises: Promise<any>[] = [
        updateRobot(robotId, { name, description: description || undefined }),
      ];
      if (thumbnailBlobRef.current) {
        promises.push(
          uploadThumbnail(robotId, thumbnailBlobRef.current).catch((err) =>
            console.warn('Thumbnail upload failed:', err),
          ),
        );
      }
      await Promise.all(promises);
      navigate(`/robots/${robotId}`);
    } catch {
      setError('Failed to save robot.');
    }
  };

  const getChecklistIcon = (status: ChecklistItem['status']) => {
    switch (status) {
      case 'done':
        return <CheckCircleIcon sx={{ color: '#66bb6a' }} />;
      case 'active':
        return <CircularProgress size={20} sx={{ color: '#EF8997' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: '#ef5350' }} />;
      default:
        return <HourglassEmptyIcon sx={{ color: 'rgba(255,255,255,0.3)' }} />;
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      {phase !== 'viewer' && (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/robots')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h3" sx={{ fontWeight: 600 }}>
            Add Robot
          </Typography>
        </Box>
      )}

      {error && phase !== 'converting' && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          <LinkedError message={error} />
        </Alert>
      )}

      {/* Phase: Drop */}
      {phase === 'drop' && (
        <Box sx={{ maxWidth: 720, mx: 'auto' }}>
          {/* GitHub Import (primary) */}
          <Box
            sx={{
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 3,
              p: 4,
              backgroundColor: 'rgba(255,255,255,0.03)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <GitHubIcon sx={{ fontSize: 28, color: 'rgba(255,255,255,0.7)' }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Import from GitHub
              </Typography>
            </Box>
            <GitHubRepoPicker
              onSelect={(url, ref) => {
                setGithubUrl(url);
                setGithubRef(ref);
              }}
              value={githubUrl}
            />
            <Box sx={{ mt: 2 }} />
            <Button
              variant="contained"
              disabled={!githubUrl.trim() || importing}
              onClick={async () => {
                if (!githubUrl.startsWith('https://github.com/')) {
                  setError('URL must start with https://github.com/');
                  return;
                }
                setError(null);
                setImporting(true);
                try {
                  const res = await importRobotFromGithub(githubUrl, githubRef || undefined);
                  setRobotId(res.id);
                  setRobot({ id: res.id, name: res.name, status: res.status } as Robot);
                  // Derive name from repo URL
                  const repoName = githubUrl.replace(/\.git$/, '').split('/').pop() || res.name;
                  setName(repoName);
                  setPhase('save');
                } catch (e: any) {
                  const msg = e?.response?.data?.detail || 'Import failed. Please check the URL and try again.';
                  setError(msg);
                } finally {
                  setImporting(false);
                }
              }}
              sx={{
                backgroundColor: '#EF8997',
                '&:hover': { backgroundColor: '#e57383' },
              }}
            >
              {importing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Import'}
            </Button>
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'rgba(255,255,255,0.4)' }}>
              We'll find the URDF and mesh files automatically
            </Typography>
          </Box>

          {/* Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', my: 3, gap: 2 }}>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.08)' }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>or</Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.08)' }} />
          </Box>

          {/* ZIP Upload (secondary) */}
          <Box
            {...getRootProps()}
            sx={{
              border: '1px solid',
              borderColor: isDragActive ? '#EF8997' : 'rgba(255,255,255,0.08)',
              borderRadius: 3,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? 'rgba(239, 137, 151, 0.04)' : 'rgba(255,255,255,0.02)',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.15)',
                backgroundColor: 'rgba(255,255,255,0.04)',
              },
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 28, color: 'rgba(255,255,255,0.2)', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              {isDragActive ? 'Drop file here' : 'Upload ZIP or URDF file'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
              .zip, .urdf, .usd, or .usda — up to 100 MB
            </Typography>
          </Box>
        </Box>
      )}

      {/* Phase: Converting */}
      {phase === 'converting' && (
        <Box sx={{ maxWidth: 500, mx: 'auto' }}>
          <List>
            {checklist.map((item) => (
              <ListItem key={item.label}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {getChecklistIcon(item.status)}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    color: item.status === 'pending' ? 'text.secondary' : 'text.primary',
                  }}
                />
              </ListItem>
            ))}
          </List>
          {error && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Alert severity="error" sx={{ mb: 2 }}><LinkedError message={error} /></Alert>
              <Button
                variant="outlined"
                onClick={() => {
                  revokeBlobUrls(meshBlobUrlsRef.current);
                  setMeshBlobUrls(new Map());
                  setPhase('drop');
                  setError(null);
                  setUrdfContent(undefined);
                  setChecklist([
                    { label: 'Upload complete', status: 'pending' },
                    { label: 'Parsing URDF', status: 'pending' },
                    { label: 'Resolving meshes', status: 'pending' },
                    { label: 'Converting to USD', status: 'pending' },
                  ]);
                }}
              >
                Try Again
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Phase: Viewer (full-screen) */}
      {phase === 'viewer' && (
        <Box
          sx={{
            position: 'relative',
            mx: -4,
            mt: -3,
            height: 'calc(100vh)',
          }}
        >
          <RobotViewer3D
            ref={viewerRef}
            urdfContent={urdfContent}
            meshBlobUrls={meshBlobUrls}
            selectedJoint={testMode ? currentTestJoint?.name : selectedJoint}
            jointValues={jointValues}
            animatingJoint={animatingJoint}
            focusJointName={testMode ? currentTestJoint?.name ?? null : selectedJoint ?? null}
            fullScreen
            panelOpenRight={(!testMode && !inspectorCollapsed) || (testMode && fixingJoint)}
          />

          {!testMode && (
            <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 5 }}>
              <IconButton
                onClick={() => navigate('/robots')}
                sx={{
                  backgroundColor: 'rgba(10, 10, 10, 0.6)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': { backgroundColor: 'rgba(10, 10, 10, 0.8)' },
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {!testMode && (
            <Box sx={{ position: 'absolute', bottom: 16, right: 16, zIndex: 5 }}>
              <Button
                variant="contained"
                onClick={async () => {
                  if (viewerRef.current) {
                    const blob = await viewerRef.current.captureScreenshot();
                    thumbnailBlobRef.current = blob;
                  }
                  setPhase('save');
                }}
                sx={{
                  backgroundColor: '#EF8997',
                  '&:hover': { backgroundColor: '#e57383' },
                  boxShadow: '0 4px 20px rgba(239, 137, 151, 0.3)',
                }}
              >
                Save Robot
              </Button>
            </Box>
          )}

          {!testMode && (
            <FloatingJointInspector
              joints={parsedJoints.length > 0 ? parsedJoints : (robot?.joints || [])}
              selectedJoint={selectedJoint}
              onSelectJoint={setSelectedJoint}
              jointValues={jointValues}
              onJointValueChange={(n, v) => setJointValues((prev) => ({ ...prev, [n]: v }))}
              collapsed={inspectorCollapsed}
              onToggleCollapse={() => setInspectorCollapsed((prev) => !prev)}
              onResetAll={() => setJointValues({})}
              articulableCount={articulableJoints.length}
            />
          )}

          {testMode && (
            <JointTestOverlay
              joints={articulableJoints}
              currentIndex={testJointIndex}
              results={testResults}
              fixes={jointFixes}
              onPass={handlePass}
              onStartFix={handleStartFix}
              onExit={handleExitTest}
              fixing={fixingJoint}
              fixLimits={fixLimits}
              onFixLimitsChange={(lower, upper) => setFixLimits({ lower, upper })}
              axisFlipped={fixAxisFlipped}
              onFlipAxis={() => setFixAxisFlipped((prev) => !prev)}
              fixContinuous={fixContinuous}
              onToggleContinuous={() => setFixContinuous((prev) => !prev)}
              onApplyFix={handleApplyFix}
              onSkipFix={handleSkipFix}
            />
          )}
        </Box>
      )}

      {/* Phase: Save */}
      {phase === 'save' && (
        <Box sx={{ maxWidth: 500, mx: 'auto' }}>
          <TextField
            fullWidth
            label="Robot Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 3 }}
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            sx={{ mb: 3 }}
          />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => setPhase(urdfContent ? 'viewer' : 'drop')}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!name.trim()}
              sx={{
                backgroundColor: '#EF8997',
                '&:hover': { backgroundColor: '#e57383' },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RobotUpload;
